// Ekran 06 — inceleme panelinin gövdesi (div.insp-b).
//
// Bölümler ve SIRALARI ref_files/btys-erp-06-uretim-beyani.html:565-598'den:
//   1 Elektrik Dengesi (koyu .inst)      4 Tesis ↔ Ünite Mutabakatı (.mini-list)
//   2 Gaz Dengesi      (koyu .inst)      5 Besleme Mutabakatı      (.mini-list)
//   3 Gaz Kalitesi     (.mini-list)      6 Olay Kayıtları          (.mini-list)
//
// İki bölüm tipi var ve ayrımı anlamlı: koyu kartlar bir DENGE denklemini
// (kalemler alt alta, sonuç en altta) gösterir; açık .mini-list bölümleri iki
// kaynağı KARŞILAŞTIRIR. Hepsi tek kalıba indirilince "net üretim", "meşale
// kaybı", "depo değişimi" gibi ara kalemler ekrandan düşüyordu.

import type { ReactNode } from 'react'
import { tr } from '../../lib/trFormat'
import type { BeyanGunuDto, MutabakatDto, MutabakatKarti } from './api'
import {
  enBuyuk, enKotuBant, esikDisi, gazSatiri, ortalama, tesisToplam, toplaBos,
} from './hesap'

type DengeSatiri = { etiket: string; deger: ReactNode; sinif?: string }

/** Koyu denge kartı — .insp-sec > .inst > N × .inst-row (+ .inst-note). */
function DengeKarti({
  baslik, satirlar, not,
}: { baslik: string; satirlar: DengeSatiri[]; not?: string | null }) {
  return (
    <div className="insp-sec">
      <div className="inst">
        <div className="inst-t">{baslik}</div>
        {satirlar.map((s) => (
          <div className="inst-row" key={s.etiket}>
            <span className="inst-l">{s.etiket}</span>
            <span className={s.sinif ? `inst-v ${s.sinif}` : 'inst-v'}>{s.deger}</span>
          </div>
        ))}
        {not && <div className="inst-note">{not}</div>}
      </div>
    </div>
  )
}

/** Açık karşılaştırma bölümü — .insp-sec > .insp-sec-t + .mini-list. */
function Bolum({ baslik, children }: { baslik: string; children: ReactNode }) {
  return (
    <div className="insp-sec">
      <div className="insp-sec-t">{baslik}</div>
      <div className="mini-list">{children}</div>
    </div>
  )
}

/** Gaz kalitesi satırı — bandı sunucu verir, eşik burada YAZILMAZ. */
function GazSatiri({ etiket, deger, birim, basamak = 1, uyari = false }: {
  etiket: string; deger: number | null; birim: string; basamak?: number; uyari?: boolean
}) {
  return (
    <div className="mini-li">
      {etiket}
      <span className={uyari ? 'vv minus' : 'vv'}>
        {deger === null ? '—' : `${tr(deger, basamak)} ${birim}`}
      </span>
    </div>
  )
}

export default function GunOzetiPaneli({
  gun, mutabakat,
}: { gun: BeyanGunuDto; mutabakat: MutabakatDto | undefined }) {
  const uretim = tesisToplam(gun, 'uretim')
  const ic = tesisToplam(gun, 'ic_ihtiyac')
  const satis = tesisToplam(gun, 'satis')
  const gazTuketim = tesisToplam(gun, 'gaz_tuketim')

  const net = uretim === null ? null : uretim - (ic ?? 0)
  const deng = toplaBos(gun.dengesizlik)
  const dengPct = deng !== null && uretim ? (Math.abs(deng) / Math.abs(uretim)) * 100 : null
  const dengBant = enKotuBant(gun.dengesizlikBant)

  const mesale = toplaBos(
    gun.olaylar.filter((o) => o.tip === 'mesale').map((o) => o.deger),
  )

  const h2s = gazSatiri(gun, 'H₂S')
  const h2sAsim = esikDisi(h2s)

  // K17 toleransı ayar_esik'ten; tanımsızsa sapma YORUMLANMAZ (⚠ basılmaz).
  const tolerans = mutabakat?.elektrik.tolerans ?? null

  return (
    <div className="insp-b">
      <DengeKarti
        baslik="Elektrik Dengesi (kWh)"
        satirlar={[
          { etiket: 'Toplam üretim — motorlardan', deger: tr(uretim, 0) },
          { etiket: 'İç ihtiyaç', deger: `−${tr(ic, 0)}` },
          { etiket: 'Net üretim', deger: tr(net, 0) },
          { etiket: 'Satış (şebeke)', deger: `−${tr(satis, 0)}` },
          {
            etiket: 'Dengesizlik ±',
            sinif: dengBant === 'kritik' ? 'red' : dengBant === 'izle' ? 'amber' : undefined,
            deger: (
              <>
                {deng !== null && deng > 0 ? '+' : ''}{tr(deng, 0)}
                {dengPct !== null && <small> %{tr(dengPct, 1)}</small>}
              </>
            ),
          },
        ]}
        not={
          `Tolerans: %${tr(gun.dengesizlikAmber, 1)} normal · ` +
          `%${tr(gun.dengesizlikAmber, 1)}–${tr(gun.dengesizlikKirmizi, 1)} izle · ` +
          `%${tr(gun.dengesizlikKirmizi, 1)}+ kritik (K17)`
        }
      />

      <DengeKarti
        baslik="Gaz Dengesi (Nm³)"
        satirlar={[
          { etiket: 'Üretilen — çürütücüler', deger: tr(mutabakat?.gazDengesi.sol ?? null, 0) },
          { etiket: 'Tüketilen — KJ + KZ', deger: `−${tr(gazTuketim, 0)}` },
          {
            etiket: 'Meşale kaybı (K24)',
            sinif: mesale ? 'amber' : undefined,
            deger: `−${tr(mesale ?? 0, 0)}`,
          },
          {
            etiket: 'Depo değişimi Δ',
            deger: mutabakat?.gazDengesi.depoDegisimi == null
              ? '—'
              : `${mutabakat.gazDengesi.depoDegisimi > 0 ? '+' : ''}${tr(mutabakat.gazDengesi.depoDegisimi, 0)}`,
          },
        ]}
      />

      <div className="insp-sec">
        <div className="insp-sec-t">Gaz Kalitesi — GA-1 (gün ort.)</div>
        <div className="mini-list">
          <GazSatiri etiket="CH₄" deger={ortalama(gazSatiri(gun, 'CH₄'))} birim="%" />
          <GazSatiri etiket="CO₂" deger={ortalama(gazSatiri(gun, 'CO₂'))} birim="%" />
          <GazSatiri
            etiket="H₂S (gün maks.)"
            deger={enBuyuk(h2s)}
            birim="ppm"
            basamak={0}
            uyari={h2sAsim}
          />
          <GazSatiri etiket="O₂" deger={ortalama(gazSatiri(gun, 'O₂'))} birim="%" />
        </div>
        {h2sAsim && (
          <div className="fld-hint tesis" style={{ marginTop: 5 }}>
            ⚠ H₂S eşik üstü — aktif karbon filtresi kontrol edilmeli
          </div>
        )}
      </div>

      <Bolum baslik="Tesis ↔ Ünite Mutabakatı (K17)">
        {([
          ['Üretim', 'uretim', uretim, 'kWh'],
          ['Satış', 'satis', satis, 'kWh'],
          ['Gaz tüketimi', 'gaz_tuketim', gazTuketim, 'Nm³'],
        ] as const).map(([etiket, tip, beyan, birim]) => {
          const uniteToplami = gun.tesis.find((t) => t.tip === tip)?.uniteToplami ?? null
          const fark = beyan === null || uniteToplami === null || uniteToplami === 0
            ? null
            : (Math.abs(beyan - uniteToplami) / Math.abs(uniteToplami)) * 100
          const asim = fark !== null && tolerans !== null && fark > tolerans
          return (
            <div className="mini-li" key={tip}>
              {etiket}
              <span className="cell-sub" style={{ marginLeft: 6 }}>
                beyan {tr(beyan, 0)} · ünite {tr(uniteToplami, 0)} {birim}
              </span>
              <span className={asim ? 'vv minus' : 'vv'}>
                {asim ? '⚠ ' : ''}{fark === null ? '—' : `%${tr(fark, 1)}`}
              </span>
            </div>
          )
        })}
      </Bolum>

      <Bolum baslik="Besleme Mutabakatı (K9 · K20)">
        {mutabakat ? <BeslemeSatirlari kart={mutabakat.besleme} /> : <Hesaplaniyor />}
      </Bolum>

      <Bolum baslik="Olay Kayıtları — Gün">
        {gun.olaylar.length === 0 ? (
          <div className="mini-li" style={{ color: 'var(--faint)' }}>Kayıt yok</div>
        ) : (
          gun.olaylar.map((o) => (
            <div className="mini-li" key={o.id}>
              <span className="dt">{String(o.saat).padStart(2, '0')}:00</span>
              {o.uniteKod ? `${o.uniteKod} · ` : ''}{o.aciklama ?? o.tip}
              <span className="vv">{tr(o.deger, 1)} {o.birim}</span>
            </div>
          ))
        )}
      </Bolum>
    </div>
  )
}

/**
 * K9 · K20 karşılaştırması. Sayılar ve tolerans MOTORDAN geliyor; referansın
 * sabit 0,985 katsayısı ve "%1,5" metni taşınmadı — o, HTML'in demo verisiydi.
 */
function BeslemeSatirlari({ kart }: { kart: MutabakatKarti }) {
  const kotu = kart.bant === 'kritik' || kart.bant === 'izle'
  return (
    <>
      <div className="mini-li">
        {kart.solEtiket}<span className="vv">{tr(kart.sol, 1)}</span>
      </div>
      <div className="mini-li">
        {kart.sagEtiket}<span className="vv">{tr(kart.sag, 1)}</span>
      </div>
      <div className="mini-li">
        Fark
        <span className={kotu ? 'vv minus' : 'vv'}>
          {kart.farkYuzde === null ? '—' : `%${tr(kart.farkYuzde, 1)}`}
          {/* Hüküm ancak bant belliyse yazılır: "hesaplanamadi" iken "tolerans
              dışı" demek uydurma olurdu (bkz. MutabakatServisi.Kart). */}
          {kart.tolerans !== null && kart.bant !== 'hesaplanamadi' &&
            ` · tolerans ${kart.bant === 'saglikli' ? 'içi' : 'dışı'} (≤%${tr(kart.tolerans, 0)})`}
        </span>
      </div>
      {kart.not && (
        <div className="mini-li" style={{ color: 'var(--faint)' }}>{kart.not}</div>
      )}
    </>
  )
}

function Hesaplaniyor() {
  return <div className="mini-li" style={{ color: 'var(--faint)' }}>Hesaplanıyor…</div>
}
